/**
 * Section splicing utilities for lease document inline editing.
 *
 * These are pure functions (no DOM, no React) extracted from Step8ReviewPreview.tsx
 * so they can be unit-tested without browser or component setup.
 */

export interface LeaseSection {
  /** null for preamble (content before the first heading) */
  heading: string | null;
  /** 0 for preamble, 1/2/3 for #/##/### headings */
  level: number;
  /** full Markdown text for this section (including heading line) */
  content: string;
  startIndex: number;
  endIndex: number;
}

/**
 * Split Markdown into heading-delimited sections.
 * Section 0 is the preamble (content before the first heading).
 */
export function splitIntoSections(md: string): LeaseSection[] {
  const sections: LeaseSection[] = [];
  const lines = md.split('\n');

  let currentStart = 0;
  let currentContent: string[] = [];
  let currentHeading: string | null = null;
  let currentLevel = 0;

  const flushSection = (endIndex: number) => {
    if (currentContent.length === 0 && currentHeading === null) return;
    const content = currentContent.join('\n');
    if (content.trim() || currentHeading !== null) {
      sections.push({
        heading: currentHeading,
        level: currentLevel,
        content,
        startIndex: currentStart,
        endIndex,
      });
    }
  };

  let charIndex = 0;

  for (const line of lines) {
    const headingMatch = line.match(/^(#{1,3}) (.+)$/);
    if (headingMatch) {
      // Flush previous section
      flushSection(charIndex);
      currentStart = charIndex;
      currentHeading = headingMatch[2] ?? null;
      currentLevel = headingMatch[1]?.length ?? 1;
      currentContent = [line];
    } else {
      currentContent.push(line);
    }
    charIndex += line.length + 1; // +1 for the \n
  }

  // Flush last section
  flushSection(charIndex);

  return sections;
}

/**
 * Position-independent "modified" check for a single section.
 *
 * A section is considered modified iff its exact content does NOT appear anywhere
 * in the original sections. Comparing by array index instead cascades false
 * positives onto every unchanged section below an insert/delete/reorder.
 */
export function isContentNew(content: string, originalSections: LeaseSection[]): boolean {
  return !originalSections.some(o => o.content === content);
}

/**
 * Replace the content of a section in the full document string.
 */
export function spliceSection(
  fullMd: string,
  sectionStartIndex: number,
  sectionEndIndex: number,
  newContent: string,
): string {
  return fullMd.slice(0, sectionStartIndex) + newContent + fullMd.slice(sectionEndIndex);
}
